import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { listRejectionReasons, type RejectionReasonOption } from '../../api/rejectionReasons.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { billRefLabel, type BillRef } from '../../../../shared/contracts/bill-ref.js';
import { TaxInvoice } from '../../../../shared/components/TaxInvoice';
import type { TaxInvoiceDoc } from '../../../../shared/contracts/tax-invoice.js';
import { loadBillDocument } from '../billing/billDocument.js';

/** The three checks a forwarded bill passes, in order, before it is paid. */
export type ReviewStage = 'commercial' | 'accounts' | 'finance';
export type ReviewDecision = 'APPROVE' | 'REJECT' | 'RETURN' | 'HOLD' | 'ESCALATE';

/** A review still being worked (M14_OPEN_REVIEW_STATUSES). */
const OPEN_STATUSES: readonly string[] = ['PENDING', 'IN_PROGRESS', 'ESCALATED', 'ON_HOLD'];

/** The M05 reasons (module M14) each decision that needs one is given from. */
const REASON_PREFIX: Partial<Record<ReviewDecision, string>> = {
  RETURN: 'R-M14-RET-',
  REJECT: 'R-M14-REJ-',
};

interface Review {
  readonly reviewId: string;
  readonly reviewStatus: string;
  readonly decision?: string | null;
}

/** The bill as GET /v1/approval/claim/:claimId gives it. */
interface BillUnderReview {
  readonly bill: BillRef | null;
  /** The check it waits on; null once it has left Sales Accounts. */
  readonly nextCheck: string | null;
}

const BUTTON_COLOUR: Record<ReviewDecision, string> = {
  APPROVE: 'var(--color-success)',
  REJECT: 'var(--color-danger)',
  RETURN: 'var(--color-warning)',
  HOLD: 'var(--color-warning)',
  ESCALATE: 'var(--color-accent-purple)',
};
const FIELD: React.CSSProperties = {
  width: 412, padding: 6, borderRadius: 4, border: '1px solid var(--color-border-default)',
};

/**
 * One check of a forwarded bill - commercial, accounts or the finance
 * approval - as all three are worked: the bill itself, the review open on it
 * (shown again when the page is reloaded), and the decision with the checks
 * ticked, the reason from the M05 master and the remarks. Starting the check
 * sends only the bill; everything the review records about it is read from
 * the bill by the server.
 */
export function BillReviewStage({
  stage,
  checks,
  decisions,
}: {
  readonly stage: ReviewStage;
  /** The checks this stage records, as the server names them. */
  readonly checks: readonly string[];
  /** The decisions this stage may take. */
  readonly decisions: readonly ReviewDecision[];
}): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  const { claimId } = useParams<{ claimId: string }>();
  const { token } = useAuth();
  const [under, setUnder] = useState<BillUnderReview | null>(null);
  // Bumped by a decision, so the bill is read again with its new status.
  const [decided, setDecided] = useState(0);
  // undefined while unread; null when no review of this check is open.
  const [review, setReview] = useState<Review | null | undefined>();
  const [submitted, setSubmitted] = useState<Review | null>(null);
  const [doc, setDoc] = useState<TaxInvoiceDoc | null>(null);
  // What could not be read is said, each thing on its own; what a click
  // could not do is said apart from it.
  const [loadErrors, setLoadErrors] = useState<readonly string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Record<string, boolean>>(
    () => Object.fromEntries(checks.map((k) => [k, false])),
  );
  const [remarks, setRemarks] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [reasons, setReasons] = useState<readonly RejectionReasonOption[]>([]);

  useEffect(() => {
    let alive = true;
    const failed = (e: unknown) => { if (alive) setLoadErrors((l) => [...l, (e as Error).message]); };
    listRejectionReasons(token, 'M14').then((r) => { if (alive) setReasons(r); }).catch(failed);
    apiRequest<Review[]>(`/v1/approval/${stage}/claim/${claimId}`, { token })
      .then((rows) => {
        if (alive) setReview((Array.isArray(rows) ? rows : []).find((r) => OPEN_STATUSES.includes(r.reviewStatus)) ?? null);
      })
      .catch(failed);
    return () => { alive = false; };
  }, [stage, claimId, token]);

  useEffect(() => {
    let alive = true;
    const failed = (e: unknown) => { if (alive) setLoadErrors((l) => [...l, (e as Error).message]); };
    apiRequest<BillUnderReview>(`/v1/approval/claim/${claimId}`, { token })
      .then((d) => { if (alive) setUnder({ bill: d.bill, nextCheck: d.nextCheck }); })
      .catch(failed);
    loadBillDocument(claimId as string, token).then((d) => { if (alive) setDoc(d); }).catch(failed);
    return () => { alive = false; };
  }, [claimId, token, decided]);

  const bill = under ? billRefLabel(under.bill, day) : '';
  const inTurn = under?.nextCheck === stage.toUpperCase();
  const alerts = [...loadErrors, error].filter((m): m is string => !!m);

  const reasonGroups = decisions
    .filter((d) => REASON_PREFIX[d])
    .map((d) => ({ decision: d, options: reasons.filter((r) => r.reasonCode.startsWith(REASON_PREFIX[d] as string)) }))
    .filter((g) => g.options.length > 0);

  const start = async () => {
    setError(null);
    try {
      setReview(await apiRequest<Review>(`/v1/approval/${stage}/start`, { token, method: 'POST', body: { claimId } }));
    } catch (e) { setError((e as Error).message); }
  };

  const decide = async (decision: ReviewDecision) => {
    if (!review) return;
    setError(null);
    const prefix = REASON_PREFIX[decision];
    const selected = prefix ? reasons.find((r) => r.reasonCode === reasonCode && r.reasonCode.startsWith(prefix)) : undefined;
    if (prefix && !selected) { setError(t('fa.review.errReason', { kind: t(`fa.review.reasonKind.${decision}`) })); return; }
    if ((prefix || decision === 'ESCALATE') && !remarks.trim()) { setError(t(`fa.review.errRemarks.${decision}`)); return; }
    if (decision === 'HOLD' && !holdReason.trim()) { setError(t('fa.review.errHoldReason')); return; }
    try {
      const r = await apiRequest<Review>(`/v1/approval/${stage}/${review.reviewId}/decision`, {
        token,
        method: 'POST',
        body: {
          decision,
          ...ticked,
          // The reason in the words the master gives it - the reader of the
          // remarks (the vendor among them) never needs its code.
          remarks: selected
            ? `${selected.reasonDescription} — ${remarks.trim()}`
            : (remarks.trim() || undefined),
          holdReason: decision === 'HOLD' ? holdReason.trim() : undefined,
        },
      });
      setSubmitted(r);
      setDecided((n) => n + 1);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <main aria-label={`${stage}-review`} style={{ padding: 24 }}>
      <div style={{ marginBottom: 12 }}><Link to="/financial-approval">{t('fa.review.backWorklist')}</Link></div>
      <h1>{t(`fa.review.title.${stage}`, { bill })}</h1>
      {alerts.length > 0 && <p role="alert" style={{ color: 'var(--color-danger)' }}>{alerts.join(' · ')}</p>}

      {review === undefined && loadErrors.length === 0 && <p>{t('fa.review.loading')}</p>}
      {review === null && !submitted && under && inTurn && (
        <button type="button" onClick={start} style={{ padding: '8px 24px', background: 'var(--color-text-link)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
          {t(`fa.review.start.${stage}`)}
        </button>
      )}
      {review === null && !submitted && under && !inTurn && (
        <p role="status" aria-label="not-in-turn">
          {under.nextCheck
            ? t('fa.review.waitsOn', { check: t(`fa.dash.stage.${under.nextCheck}`) })
            : t('fa.review.notWithSalesAccounts')}
        </p>
      )}

      {review && !submitted && (
        <section aria-label="review-decision">
          <p>{t('fa.review.status', { status: review.reviewStatus })}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 412, marginBottom: 16 }}>
            {checks.map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={ticked[k]} onChange={(e) => setTicked({ ...ticked, [k]: e.target.checked })} />
                {t(`fa.review.check.${k}`)}
              </label>
            ))}
          </div>
          {reasonGroups.length > 0 && (
            <label>{t('fa.review.reasonLabel')}<br />
              <select data-testid={`${stage}-reason-select`} value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} style={FIELD}>
                <option value="">{t('fa.review.selectReason')}</option>
                {reasonGroups.map((g) => (
                  <optgroup key={g.decision} label={t(`fa.review.optgroup.${g.decision}`)}>
                    {g.options.map((r) => <option key={r.reasonId} value={r.reasonCode}>{r.reasonDescription}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          )}
          <br />
          <label>{t('fa.review.remarksLabel')}<br />
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} style={{ ...FIELD, width: 400, height: 60 }} />
          </label>
          {decisions.includes('HOLD') && (
            <>
              <br />
              <label>{t('fa.review.holdReasonLabel')}<br />
                <input value={holdReason} onChange={(e) => setHoldReason(e.target.value)} style={{ ...FIELD, width: 400 }} />
              </label>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {decisions.map((d) => (
              <button key={d} type="button" onClick={() => decide(d)}
                style={{ padding: '8px 20px', background: BUTTON_COLOUR[d], color: 'var(--color-text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
                {t(`fa.review.decision.${d}`)}
              </button>
            ))}
          </div>
        </section>
      )}

      {submitted && (
        <div role="status" style={{ marginTop: 16, padding: 16, border: '1px solid var(--color-border-default)', borderRadius: 8 }}>
          {t('fa.review.submitted', {
            decision: submitted.decision ? t(`fa.review.decision.${submitted.decision}`) : '',
            status: submitted.reviewStatus,
          })}
        </div>
      )}

      <section aria-label="the-bill" style={{ marginTop: 24 }}>
        <h2>{t('fa.review.theBill')}</h2>
        {doc ? <TaxInvoice doc={doc} /> : loadErrors.length === 0 && <p>{t('fa.review.loading')}</p>}
      </section>
    </main>
  );
}
